/**
 * TerrainSockel — geschlossener Modellkörper unter dem Regions-Cut-out.
 *
 * Alex (2026-08-15, Handy-Screenshot 119497): „Am Rand hängt alles in der
 * Luft — Seitenwand, die unten flach abschließt." Wie beim Museums-
 * Reliefmodell: entlang des Regions-UMRISSES (Vektor-Polygon, dieselbe
 * Quelle wie die Cut-out-Maske) eine senkrechte Wand — Oberkante folgt dem
 * Gelände (getWorldY, leicht ins Terrain überhöht, damit am Masken-Saum
 * kein Spalt aufreißt), Unterkante flach auf einer Basis-Höhe — plus eine
 * Bodenplatte (ShapeUtils-Triangulation), damit der Körper auch von unten
 * geschlossen ist.
 *
 * Da die Kachel-Höhen streamen, wird die Wand-Oberkante wie die Routen
 * periodisch nachgeführt, bis sie stabil ist.
 */
import * as THREE from 'three';
import { SceneNode } from '../core/SceneNode';
import type { TerrainRoot } from './TerrainRoot';

export interface TerrainSockelConfig {
    terrain: TerrainRoot;
    /** JSON mit { ringe: [ [ [lon,lat], ... ] ] } — Ring 0 = Außenkontur */
    umrissUrl: string;
    /** Basis-Höhe der Unterkante in Metern (Vorgabe 100 m — unter allem) */
    basisMeter?: number;
    /** Überhöhung der Wand-Oberkante ins Terrain hinein (Meter) */
    ueberstandMeter?: number;
    /** Resample-Schrittweite entlang des Umrisses in Welt-Units */
    schritt?: number;
    farbe?: string;
}

export class TerrainSockel extends SceneNode {
    private readonly _cfg: Required<TerrainSockelConfig>;
    private readonly _terrain: TerrainRoot;
    private _punkte: { x: number; z: number }[] = [];
    private _wand: THREE.Mesh | null = null;
    private _drapeUhr = 0;
    private _letzteSumme = NaN;
    private _versuche = 0;

    constructor(cfg: TerrainSockelConfig) {
        super('TerrainSockel');
        this._cfg = {
            basisMeter: 100,
            ueberstandMeter: 60,
            schritt: 0.04,          // 200 m bei 1u=5km
            farbe: '#b03a2e',       // Modell-Rot (Alex 2026-08-15)
            ...cfg,
        };
        this._terrain = cfg.terrain;
        this._mesh = new THREE.Group();
        this._mesh.name = 'TerrainSockel';
        this._mesh.userData.sceneNode = this;
        void this._lade().catch((error) => {
            console.error('[terrain] Sockel-Aufbau fehlgeschlagen:', error);
        });
    }

    protected calculateBoundingBox(): THREE.Box3 {
        const box = new THREE.Box3();
        if (this._mesh) box.setFromObject(this._mesh);
        if (box.isEmpty()) box.setFromCenterAndSize(new THREE.Vector3(), new THREE.Vector3(1, 1, 1));
        return box;
    }

    private async _lade(): Promise<void> {
        const daten = await fetch(this._cfg.umrissUrl).then((r) => r.json()).catch(() => null);
        const ring: [number, number][] = daten?.ringe?.[0];
        if (!ring || ring.length < 4) {
            console.warn('[terrain] Sockel-Umriss nicht ladbar — kein Sockel:', this._cfg.umrissUrl);
            return;
        }
        const proj = this._terrain.projection;

        // Umriss in Welt-XZ, dann aequidistant resampeln (geschlossener Ring)
        const roh = ring.map(([lon, lat]) => {
            const w = proj.geoToWorld(lon, lat, 0);
            return { x: w.x, z: w.z };
        });
        const punkte: { x: number; z: number }[] = [];
        for (let i = 0; i < roh.length; i++) {
            const a = roh[i], b = roh[(i + 1) % roh.length];
            const laenge = Math.hypot(b.x - a.x, b.z - a.z);
            const teile = Math.max(1, Math.ceil(laenge / this._cfg.schritt));
            for (let t = 0; t < teile; t++) {
                punkte.push({
                    x: a.x + ((b.x - a.x) * t) / teile,
                    z: a.z + ((b.z - a.z) * t) / teile,
                });
            }
        }
        this._punkte = punkte;

        // Bodenplatte: Umriss trianguliert, flach auf Basis-Hoehe
        const basisY = proj.hoeheToWorldY(this._cfg.basisMeter);
        const kontur = roh.map((p) => new THREE.Vector2(p.x, p.z));
        const dreiecke = THREE.ShapeUtils.triangulateShape(kontur, []);
        const bodenPos = new Float32Array(kontur.length * 3);
        kontur.forEach((p, i) => {
            bodenPos[i * 3] = p.x; bodenPos[i * 3 + 1] = basisY; bodenPos[i * 3 + 2] = p.y;
        });
        const bodenIdx = new Uint32Array(dreiecke.flat());
        const bodenGeo = new THREE.BufferGeometry();
        bodenGeo.setAttribute('position', new THREE.BufferAttribute(bodenPos, 3));
        bodenGeo.setIndex(new THREE.BufferAttribute(bodenIdx, 1));
        bodenGeo.computeVertexNormals();
        const material = new THREE.MeshStandardMaterial({
            color: this._cfg.farbe, roughness: 0.9, metalness: 0, side: THREE.DoubleSide,
        });
        const boden = new THREE.Mesh(bodenGeo, material);
        boden.name = 'sockel/boden';
        (this._mesh as THREE.Group).add(boden);

        // Wand: eigenes Mesh, wird gedrapt sobald Hoehen verfuegbar sind
        this._wand = new THREE.Mesh(new THREE.BufferGeometry(), material);
        this._wand.name = 'sockel/wand';
        this._wand.visible = false;
        (this._mesh as THREE.Group).add(this._wand);
    }

    /** Wand-Oberkante den streamenden Kachel-Hoehen nachfuehren. */
    protected onUpdate(deltaTime: number): void {
        if (!this._wand || this._punkte.length === 0 || this._versuche > 10) return;
        this._drapeUhr += deltaTime;
        if (this._drapeUhr < 2) return;
        this._drapeUhr = 0;

        const proj = this._terrain.projection;
        const basisY = proj.hoeheToWorldY(this._cfg.basisMeter);
        const extra = proj.hoeheToWorldY(this._cfg.ueberstandMeter) - proj.hoeheToWorldY(0);

        const n = this._punkte.length;
        const oben = new Float32Array(n);
        const bekannt = new Uint8Array(n);
        let summe = 0, fehlend = 0;
        for (let i = 0; i < n; i++) {
            const p = this._punkte[i];
            const y = this._terrain.getWorldY(p.x, p.z);
            if (y === null) { fehlend++; continue; }
            oben[i] = y + extra;
            bekannt[i] = 1;
            summe += y;
        }
        if (fehlend > n * 0.5) return;
        /* Fehlende Oberkanten NIE auf die Basis kollabieren lassen
         * (degenerierte Segmente = optisch springende Unterkante) —
         * stattdessen vom naechsten bekannten Nachbarn uebernehmen. */
        for (let i = 0; i < n; i++) {
            if (bekannt[i]) continue;
            for (let d = 1; d < n; d++) {
                const vor = (i - d + n) % n, nach = (i + d) % n;
                if (bekannt[vor]) { oben[i] = oben[vor]; break; }
                if (bekannt[nach]) { oben[i] = oben[nach]; break; }
            }
        }
        if (Math.abs(summe - this._letzteSumme) < 0.01) return;
        this._letzteSumme = summe;
        this._versuche++;

        // Geschlossener Wandstreifen: je Punkt oben+unten, Quads ringsum
        const positionen = new Float32Array((n + 1) * 2 * 3);
        for (let i = 0; i <= n; i++) {
            const p = this._punkte[i % n];
            const y = oben[i % n];
            const o = i * 6;
            positionen[o] = p.x; positionen[o + 1] = y; positionen[o + 2] = p.z;
            positionen[o + 3] = p.x; positionen[o + 4] = basisY; positionen[o + 5] = p.z;
        }
        const indizes = new Uint32Array(n * 6);
        let idx = 0;
        for (let i = 0; i < n; i++) {
            const a = i * 2;
            indizes[idx++] = a; indizes[idx++] = a + 1; indizes[idx++] = a + 2;
            indizes[idx++] = a + 1; indizes[idx++] = a + 3; indizes[idx++] = a + 2;
        }
        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.BufferAttribute(positionen, 3));
        geo.setIndex(new THREE.BufferAttribute(indizes, 1));
        geo.computeVertexNormals();
        geo.computeBoundingSphere();
        this._wand.geometry.dispose();
        this._wand.geometry = geo;
        this._wand.visible = true;
    }
}
